import { EnvelopeIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  EMAIL_FALLBACK,
  EMAIL_VERIFICATION_GATE,
  SEND_NEW_LINK,
  SENDING_LINK,
  verificationCheckNotice,
} from '@personal-trainer-app/shared';

import { AuthFooterLink } from '../AuthFooterLink';
import { AuthMessage } from '../AuthMessage';
import { AuthPageShell } from '../AuthPageShell';
import { EmailAddress } from '../EmailAddress';
import { EMAIL_VERIFICATION_BRAND } from '../emailVerificationBrand';
import { SendLinkFailureAlert } from '../SendLinkFailureAlert';
import { VerificationLinkSent } from '../VerificationLinkSent';

import type { EmailVerificationPromptProps } from './EmailVerificationPrompt.types';

import { Alert, Button, Text, TextLink } from '@/components/ui';

/**
 * What the unverified gate shows: the ask to verify, or a new link on its
 * way, with Check again and Sign out beneath. Presentational: the gate
 * (`EmailVerificationGate`) reads the account and runs the actions.
 */
export function EmailVerificationPrompt({
  email,
  sentAt,
  remaining,
  sending,
  failure,
  check,
  onSend,
  onCheckAgain,
  onSignOut,
  headingRef,
}: EmailVerificationPromptProps): React.JSX.Element {
  const checked = verificationCheckNotice(check);
  const checkNotice = checked ? (
    <Alert variant={checked.variant} title={checked.title} className="mb-6">
      {checked.body}
    </Alert>
  ) : null;

  const wayOn = (
    <>
      <Text variant="body-sm" color="muted" className="mt-3 text-center">
        <TextLink
          lead={EMAIL_VERIFICATION_GATE.checkLead}
          onClick={onCheckAgain}
          disabled={check === 'checking'}
        >
          {check === 'checking'
            ? EMAIL_VERIFICATION_GATE.checking
            : EMAIL_VERIFICATION_GATE.checkAgain}
        </TextLink>
      </Text>
      <AuthFooterLink
        lead={EMAIL_VERIFICATION_GATE.signOutLead}
        onClick={onSignOut}
      >
        {EMAIL_VERIFICATION_GATE.signOut}
      </AuthFooterLink>
    </>
  );

  return (
    <AuthPageShell {...EMAIL_VERIFICATION_BRAND}>
      {sentAt ? (
        <VerificationLinkSent
          email={email}
          sentAt={sentAt}
          remaining={remaining}
          sending={sending}
          failure={failure}
          notice={checkNotice}
          onResend={onSend}
          headingRef={headingRef}
        >
          {wayOn}
        </VerificationLinkSent>
      ) : (
        <AuthMessage
          icon={<EnvelopeIcon />}
          tone="accent"
          eyebrow={EMAIL_VERIFICATION_GATE.eyebrow}
          title={EMAIL_VERIFICATION_GATE.title}
          lede={
            <>
              {EMAIL_VERIFICATION_GATE.lede.before}
              <EmailAddress email={email} fallback={EMAIL_FALLBACK} />
              {EMAIL_VERIFICATION_GATE.lede.after}
            </>
          }
          headingRef={headingRef}
        >
          {failure ? (
            <SendLinkFailureAlert failure={failure} className="mb-6" />
          ) : null}
          {checkNotice}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={sending}
            onClick={onSend}
          >
            {sending ? SENDING_LINK : SEND_NEW_LINK}
          </Button>
          {wayOn}
        </AuthMessage>
      )}
    </AuthPageShell>
  );
}

export default EmailVerificationPrompt;
